import {APIV1Version, ApiState} from '@buncha/common/types/common';
import {
  AsyncOrderStatus,
  CheckAsyncOrderStatusRes,
  GetOrdersForValidationAndUpdationCheckoutRes,
  OrderValidationUpdateRes,
  OrderVerifyStatus,
  OrdersForUpdation,
  OrdersForValidationAndUpdationCheckout,
  UpdateB2BOrderRes,
} from '../types';
import {
  CANCEL_ORDER,
  ORDER_VALIDATION_UPDATE,
  SET_DELIVERY_LOCATION_STATUS_ORDERS,
  UPDATE_B2B_ORDER,
} from '@buncha/config/graphQL/documentNode/mutation';
import {
  CHECK_ASYNC_ORDER_STATUS,
  GET_ORDERS_FOR_VALIDATION_AND_UPDATION_CHECKOUT,
  GET_ORDER_FOR_ORDER_INFO,
} from '@buncha/config/graphQL/documentNode/queries';
import {
  DeliveryLocationStatus,
  SetDeliveryLocationStatusOrdersResponse,
} from '@buncha/batchDetails/types';
import {Dispatch, useCallback, useState} from 'react';
import {
  GetOrderForOrderInfoResponse,
  OrderForOrderInfo,
} from '../types/B2BBarcode';

import {B2BCheckoutActionsLocal} from './B2BCheckoutReducer';
import {ToastType} from '@buncha/components/composites/notification/type';
import {gqlService} from '@buncha/config/graphQL';
import {useToastMessage} from '@buncha/components/composites/notification';

export function useGetOrdersForValidationAndUpdationCheckout(
  version: APIV1Version,
  dispatch: Dispatch<any> | null,
): [
  (runId: number) => Promise<void>,
  ApiState<OrdersForValidationAndUpdationCheckout>,
  boolean,
] {
  const [state, setState] = useState<
    ApiState<OrdersForValidationAndUpdationCheckout>
  >({
    data: undefined,
    error: undefined,
    loading: false,
  });
  const [initPage, setInitPage] = useState(true);
  const func = useCallback(
    async (runId: number) => {
      try {
        setState({loading: true, error: undefined, data: undefined});
        const result =
          await gqlService?.query<GetOrdersForValidationAndUpdationCheckoutRes>(
            {
              query: GET_ORDERS_FOR_VALIDATION_AND_UPDATION_CHECKOUT,
              variables: {
                version: version,
                requestParams: {
                  orderTypeToFetch: 'both',
                  runId: runId,
                },
              },
              fetchPolicy: 'network-only',
              context: {
                headers: {
                  'graphql-version': 'v1',
                },
              },
            },
          );
        const res = result?.data?.getOrdersForValidationAndUpdation?.data?.V100;
        if (dispatch && res) dispatch(B2BCheckoutActionsLocal.setOrders(res));
        setState({
          loading: false,
          error: undefined,
          data: res,
        });
        setInitPage(false);
      } catch (err: any) {
        setState({loading: false, error: err, data: undefined});
        setInitPage(false);
      }
    },
    [dispatch, version],
  );
  return [func, state, initPage];
}

export function useCheckAsyncOrderStatus(version: APIV1Version) {
  const [loading, setLoading] = useState(true);
  const [showErrorToast] = useToastMessage(ToastType.Error);
  const [data, setData] = useState<AsyncOrderStatus | undefined>();

  const checkAsyncOrderStatus = async (orderIds: number[]) => {
    setLoading(true);
    try {
      const response = await gqlService?.query<CheckAsyncOrderStatusRes>({
        query: CHECK_ASYNC_ORDER_STATUS,
        variables: {
          version: version,
          requestParams: {
            orderIds: orderIds,
          },
        },
        fetchPolicy: 'network-only',
        context: {
          headers: {
            'graphql-version': 'v1',
          },
        },
      });
      const res =
        response?.data?.checkAsyncOrderStatus.data.V100.asyncOrderStatus;
      setData(res);
      setLoading(false);
    } catch (err: any) {
      setLoading(false);
      showErrorToast(err?.message);
    }
  };

  return {
    checkAsyncOrderStatus,
    loading,
    data,
  };
}

export function useOrderValidationUpdate(
  version: APIV1Version,
  dispatch: Dispatch<any> | null,
): [
  (orderIds: number[], runId: number) => void,
  ApiState<OrdersForUpdation[]>,
] {
  const [state, setState] = useState<ApiState<OrdersForUpdation[]>>({
    data: undefined,
    error: undefined,
    loading: false,
  });
  const [updateB2BOrder] = useUpdateB2BOrder(APIV1Version.V100);
  const [getOrdersForValidationAndUpdation] =
    useGetOrdersForValidationAndUpdationCheckout(APIV1Version.V100, dispatch);
  const func = useCallback(
    async (orderIds: number[], runId: number) => {
      try {
        setState({loading: true, error: undefined, data: undefined});
        const result = await gqlService?.mutation<OrderValidationUpdateRes>({
          mutation: ORDER_VALIDATION_UPDATE,
          variables: {
            version: version,
            requestParams: {
              orderIds: orderIds,
            },
          },
          fetchPolicy: 'network-only',
          context: {
            headers: {
              'graphql-version': 'v1',
            },
          },
        });
        const res = result?.data?.OrderValidation?.data?.V100.orders;
        const filterIds = res
          ?.filter(order => order.status === OrderVerifyStatus.validationDone)
          .map(order => order?.order?.id);
        if (filterIds?.length) {
          await updateB2BOrder(filterIds);
          await getOrdersForValidationAndUpdation(runId);
        }

        setState({
          loading: false,
          error: undefined,
          data: res,
        });
        if (dispatch && res && !filterIds?.length)
          dispatch(B2BCheckoutActionsLocal.updateOnVerification(res));
      } catch (err: any) {
        setState({loading: false, error: err, data: undefined});
      }
    },
    [dispatch, getOrdersForValidationAndUpdation, updateB2BOrder, version],
  );
  return [func, state];
}

export function useUpdateB2BOrder(
  version: APIV1Version,
): [(orderIds: number[]) => Promise<void>, ApiState<boolean>] {
  const [state, setState] = useState<ApiState<boolean>>({
    data: undefined,
    error: undefined,
    loading: false,
  });
  const func = useCallback(
    async (orderIds: number[]) => {
      try {
        setState({loading: true, error: undefined, data: undefined});
        const result = await gqlService?.mutation<UpdateB2BOrderRes>({
          mutation: UPDATE_B2B_ORDER,
          variables: {
            version: version,
            requestParams: {
              orderIds: orderIds,
            },
          },
          fetchPolicy: 'network-only',
          context: {
            headers: {
              'graphql-version': 'v1',
            },
          },
        });
        const res = result?.data?.updateB2BOrder?.data?.V100?.status;
        setState({
          loading: false,
          error: undefined,
          data: res,
        });
      } catch (err: any) {
        setState({loading: false, error: err, data: undefined});
      }
    },
    [version],
  );
  return [func, state];
}

export function useGetOrderInfoDetails() {
  const [showErrorToast] = useToastMessage(ToastType.Error);
  const [loading, setLoading] = useState(true);
  const [orderInfoData, setOrderInfoData] = useState<
    OrderForOrderInfo | undefined
  >();
  const getOrderInfoDetails = async (orderId: string, runId: number) => {
    try {
      setLoading(true);
      const res = await gqlService?.query<GetOrderForOrderInfoResponse>({
        query: GET_ORDER_FOR_ORDER_INFO,
        fetchPolicy: 'network-only',
        variables: {
          orderId: orderId,
          runId: runId,
        },
      });
      const orderInfo = res?.data?.getOrder;
      setOrderInfoData(orderInfo);
      setLoading(false);
    } catch (err: any) {
      setLoading(false);
      showErrorToast(err?.message);
    }
  };

  return {getOrderInfoDetails, orderInfoData, loading};
}

export function useSetDeliveryLocationStatus() {
  const [showMessage] = useToastMessage(ToastType.Error);
  const [statusLoading, setStatusLoading] = useState(false);

  const setDeliveryLocationStatus = useCallback(
    async (
      runId: number,
      status: DeliveryLocationStatus,
      orderIds: number[],
      callBack: () => void,
    ) => {
      setStatusLoading(true);
      try {
        const response =
          await gqlService?.mutation<SetDeliveryLocationStatusOrdersResponse>({
            mutation: SET_DELIVERY_LOCATION_STATUS_ORDERS,
            variables: {
              runId: runId,
              status: status,
              orderIds: orderIds,
            },
            fetchPolicy: 'network-only',
          });
        if (response?.data?.setDeliveryLocationStatusOfAssignedOrders)
          callBack();
        setStatusLoading(false);
      } catch (error: any) {
        showMessage(error.message);
        setStatusLoading(false);
      }
    },
    [showMessage],
  );

  return {setDeliveryLocationStatus, statusLoading};
}

export function useCancelOrder() {
  const [showMessage] = useToastMessage(ToastType.Error);
  const [statusLoading, setStatusLoading] = useState(false);

  const cancelOrder = useCallback(
    async (runId: number, orderId: number) => {
      setStatusLoading(true);
      try {
        await gqlService?.mutation<{id: number}>({
          mutation: CANCEL_ORDER,
          variables: {
            runId: runId,
            orderId: orderId,
          },
          fetchPolicy: 'network-only',
        });
        setStatusLoading(false);
      } catch (error: any) {
        showMessage(error.message);
        setStatusLoading(false);
      }
    },
    [showMessage],
  );

  return {cancelOrder, statusLoading};
}
